// Where and how each text-bearing object's text sits: content box, alignment,
// font fitting, plus a per-object layout cache and the canvas draw routines.
import type { BoardObject, Card, RichText, Shape, Sticky, TextObj } from '../../shared/types.ts';
import { isEmptyRichText } from '../../shared/rich-text.ts';
import { CARD, localToday } from '../../shared/cards.ts';
import type { Rect } from '../util/geometry.ts';
import { fitText, layoutText, type Align, type Measure, type TextLayout } from './layout.ts';
import { drawLayout, TEXT_COLOR, type Ctx2D } from './draw.ts';
import { drawCardFace, layoutCard, NO_PEOPLE, type CardFace, type CardPeople } from '../cards/face.ts';

export { LINK_COLOR, TEXT_COLOR } from './draw.ts';
const GREEK_COLOR = 'rgba(0, 0, 0, 0.22)';
const NO_LAYOUT: TextLayout = { lines: [], width: 0, height: 0, fontSize: CARD.itemSize, lineHeight: 0, brokeWords: false };

export type TextBearing = Sticky | Shape | TextObj;

export interface TextSpec {
  box: Rect;              // content box, object-local
  align: Align;
  valign: 'top' | 'middle';
  clip: boolean;          // clip text to the box (stickies, shapes)
  fixedSize?: number;     // text objects: no fitting
  maxSize: number;
  minSize: number;
}

/** A laid-out text positioned in object-local coordinates. */
export interface PlacedText {
  layout: TextLayout;
  ox: number;             // layout origin
  oy: number;
  region: Rect;           // visible extent (clipped), used for rasterising
  color: string;
  /** Cards: the whole face (title, note, checklist, footer) is drawn and cached as one. */
  card?: CardFace;
}

export function isTextBearing(o: BoardObject): o is TextBearing {
  return o.type === 'sticky' || o.type === 'shape' || o.type === 'text';
}

export function textOf(o: BoardObject): RichText | undefined {
  return isTextBearing(o) ? o.text : undefined;
}

function shapeTextBox(o: Shape): Rect {
  const { w, h } = o;
  const pad = 6 + o.strokeWidth / 2;
  let r: Rect;
  switch (o.kind) {
    case 'rect': r = { x: pad, y: pad, w: w - 2 * pad, h: h - 2 * pad }; break;
    case 'ellipse': {
      const iw = w / Math.SQRT2, ih = h / Math.SQRT2;
      r = { x: (w - iw) / 2 + pad / 2, y: (h - ih) / 2 + pad / 2, w: iw - pad, h: ih - pad };
      break;
    }
    case 'diamond': r = { x: w / 4, y: h / 4, w: w / 2, h: h / 2 }; break;
    case 'triangle': r = { x: w / 4, y: h / 2, w: w / 2, h: h / 2 - pad }; break;
  }
  return { ...r, w: Math.max(1, r.w), h: Math.max(1, r.h) };
}

export function textSpec(o: TextBearing): TextSpec {
  switch (o.type) {
    case 'sticky': {
      const pad = Math.min(o.w, o.h) * 0.08;
      return {
        box: { x: pad, y: pad, w: Math.max(1, o.w - 2 * pad), h: Math.max(1, o.h - 2 * pad) },
        align: 'center', valign: 'middle', clip: true,
        maxSize: Math.min(o.w, o.h) * 0.14, minSize: 4,
      };
    }
    case 'shape':
      return { box: shapeTextBox(o), align: 'center', valign: 'middle', clip: true, maxSize: 18, minSize: 4 };
    case 'text':
      return {
        box: { x: 0, y: 0, w: o.w, h: o.h }, align: 'left', valign: 'top', clip: false,
        fixedSize: o.fontSize, maxSize: o.fontSize, minSize: o.fontSize,
      };
  }
}

export function layoutFor(o: TextBearing, text: RichText, measure: Measure): TextLayout {
  const spec = textSpec(o);
  return spec.fixedSize
    ? layoutText(text, { width: spec.box.w, fontSize: spec.fixedSize, align: spec.align }, measure)
    : fitText(text, spec.box.w, spec.box.h, spec.align, spec.maxSize, spec.minSize, measure);
}

function place(o: TextBearing, text: RichText, measure: Measure): PlacedText {
  const spec = textSpec(o);
  const layout = layoutFor(o, text, measure);
  const ox = spec.box.x;
  const oy = spec.box.y + (spec.valign === 'middle' ? (spec.box.h - layout.height) / 2 : 0);
  let top = oy, bottom = oy + layout.height;
  if (spec.clip) {
    top = Math.max(top, spec.box.y);
    bottom = Math.min(bottom, spec.box.y + spec.box.h);
  }
  return { layout, ox, oy, region: { x: spec.box.x, y: top, w: spec.box.w, h: Math.max(0, bottom - top) }, color: TEXT_COLOR };
}

function geometryKey(o: TextBearing): string {
  return o.type === 'shape' ? `${o.w}|${o.h}|${o.kind}|${o.strokeWidth}`
    : o.type === 'text' ? `${o.w}|${o.h}|${o.fontSize}` : `${o.w}|${o.h}`;
}

/** A card's face depends on its content, its width, who's who and today's date. */
const sameCardContent = (a: Card, b: Card) =>
  a.title === b.title && a.note === b.note && a.items === b.items && a.assignees === b.assignees && a.due === b.due && a.w === b.w;

const TODAY_CHECK_MS = 30_000;

/**
 * Per-object layout cache. Objects are immutable, so an unchanged `text`
 * reference plus unchanged geometry means the cached layout is still valid;
 * the returned PlacedText keeps its identity until then (the raster cache
 * relies on that).
 */
export class TextLayouts {
  private readonly measure: Measure;
  private readonly people: () => CardPeople;
  private readonly cache = new Map<string, { text: RichText; key: string; placed: PlacedText }>();
  private readonly cards = new Map<string, { card: Card; env: string; placed: PlacedText }>();
  private today = localToday();
  private todayCheckedAt = Date.now();

  constructor(measure: Measure, people: () => CardPeople = () => NO_PEOPLE) {
    this.measure = measure;
    this.people = people;
  }

  /** Layout for `o`, computing it if needed; null when there's no text to draw. */
  get(o: BoardObject): PlacedText | null {
    const cached = this.peek(o);
    if (cached !== undefined) return cached;
    if (o.type === 'card') {
      const people = this.people();
      const face = layoutCard(o, this.measure, people, this.today);
      const placed: PlacedText = {
        layout: NO_LAYOUT, ox: 0, oy: 0, color: TEXT_COLOR, card: face,
        region: { x: 0, y: CARD.strip, w: o.w, h: Math.max(0, face.height - CARD.strip) },
      };
      this.cards.set(o.id, { card: o, env: this.envKey(people), placed });
      return placed;
    }
    const t = o as TextBearing;
    const placed = place(t, t.text!, this.measure);
    this.cache.set(o.id, { text: t.text!, key: geometryKey(t), placed });
    return placed;
  }

  /** Like get(), but returns undefined instead of computing a missing layout. */
  peek(o: BoardObject): PlacedText | null | undefined {
    if (o.type === 'card') {
      const hit = this.cards.get(o.id);
      return hit && sameCardContent(hit.card, o) && hit.env === this.envKey(this.people()) ? hit.placed : undefined;
    }
    if (!isTextBearing(o)) return null;
    const text = o.text;
    if (!text || isEmptyRichText(text)) return null;
    const hit = this.cache.get(o.id);
    return hit && hit.text === text && hit.key === geometryKey(o) ? hit.placed : undefined;
  }

  /** A card's face (laid out now if needed). */
  cardFace(o: Card): CardFace {
    return this.get(o)!.card!;
  }

  /** How tall a card would be at another width (e.g. once a column sets it). Not cached. */
  cardHeightAt(o: Card, w: number): number {
    if (Math.abs(o.w - w) < 0.01) return this.cardFace(o).height;
    return layoutCard({ ...o, w }, this.measure, this.people(), this.today).height;
  }

  forget(id: string): void {
    this.cache.delete(id);
    this.cards.delete(id);
  }

  get size(): number { return this.cache.size + this.cards.size; }

  /** Overdue chips turn red at midnight, and avatars change when names arrive. */
  private envKey(people: CardPeople): string {
    const now = Date.now();
    if (now - this.todayCheckedAt > TODAY_CHECK_MS) {
      this.todayCheckedAt = now;
      this.today = localToday();
    }
    return `${people.version}|${this.today}`;
  }
}

/** Draws placed text in object-local coords. `scale` = device px per world unit. */
export function drawText(ctx: Ctx2D, p: PlacedText, scale: number): void {
  if (p.card) drawCardFace(ctx, p.card, scale);
  else drawLayout(ctx, p.layout, p.ox, p.oy, p.color, scale);
}

/** Low-zoom stand-in for text: a grey bar per line. */
export function drawGreeked(ctx: CanvasRenderingContext2D, p: PlacedText): void {
  const lh = p.layout.lineHeight;
  ctx.fillStyle = GREEK_COLOR;
  for (const ln of p.layout.lines) {
    if (ln.w > 0) ctx.fillRect(p.ox + ln.x, p.oy + ln.top + lh * 0.3, ln.w, lh * 0.4);
  }
}

/** The link under object-local point (lx, ly), if any. */
export function linkAt(p: PlacedText, lx: number, ly: number): string | null {
  const { layout, ox, oy } = p;
  const ln = layout.lines[Math.floor((ly - oy) / layout.lineHeight)];
  if (!ln) return null;
  for (const f of ln.frags) {
    if (f.href && lx >= ox + f.x && lx <= ox + f.x + f.w - f.trail) return f.href;
  }
  return null;
}

// --- canvas text measurement, cached ---

let measureCtx: OffscreenCanvasRenderingContext2D | null = null;
let measureFont = '';
const widths = new Map<string, number>();
const MAX_WIDTHS = 100_000;

export const canvasMeasure: Measure = (text, font) => {
  const key = `${font}\n${text}`;
  const hit = widths.get(key);
  if (hit !== undefined) return hit;
  measureCtx ??= new OffscreenCanvas(1, 1).getContext('2d')!;
  if (font !== measureFont) measureCtx.font = measureFont = font;
  const w = measureCtx.measureText(text).width;
  if (widths.size >= MAX_WIDTHS) widths.clear();
  widths.set(key, w);
  return w;
};
